// Shared between the create flow (index-create.mixin) and the edit flow (SettingsIndexSourceEditor)
// so a Qdrant XML source's field selection is merged/read identically in both.

function isStockLevelTag(tag) {
  return String(tag)
    .replace(/^[a-zA-Z]+:/, '')
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .toLowerCase() === 'stock_lvl'
}

function sanitizeFieldSelection(fieldSelection = {}) {
  return {
    embed_fields: (fieldSelection.embed_fields || []).filter(tag => !isStockLevelTag(tag)),
    payload_fields: Object.entries(fieldSelection.payload_fields || {}).reduce((fields, [tag, value]) => {
      if (!isStockLevelTag(tag)) fields[tag] = value
      return fields
    }, {}),
    group_key: (fieldSelection.group_key || []).filter(tag => !isStockLevelTag(tag))
  }
}

function categoryTagFromPayload(payloadFields = {}) {
  return Object.keys(payloadFields).find(tag => payloadFields[tag]?.as === 'category') || ''
}

function withCategoryPayload(fieldSelection, tag) {
  const payloadFields = Object.entries(fieldSelection.payload_fields).reduce((fields, [field, config]) => {
    if (config?.as !== 'category') fields[field] = config
    return fields
  }, {})
  if (!tag) {
    return { ...fieldSelection, payload_fields: payloadFields }
  }
  return {
    embed_fields: fieldSelection.embed_fields.filter(field => field !== tag),
    payload_fields: { ...payloadFields, [tag]: { as: 'category' } },
    group_key: fieldSelection.group_key.filter(field => field !== tag)
  }
}

function withoutStoredCategory(config) {
  if (!config?.tag_mapping || !Object.prototype.hasOwnProperty.call(config.tag_mapping, 'category')) {
    return config
  }
  return {
    ...config,
    tag_mapping: Object.fromEntries(
      Object.entries(config.tag_mapping).filter(([key]) => key !== 'category')
    )
  }
}

// Category is stored only as a normalized payload field, not on tag_mapping.
// Legacy configs may still have tag_mapping.category.
export function extractCategoryTag(config) {
  return config?.tag_mapping?.category || categoryTagFromPayload(config?.payload_fields) || ''
}

// Merge the per-source Qdrant field selection into an XML source config, ONLY when non-empty.
// Returns a new config (does not mutate the input). Never persists category on tag_mapping.
export function applyFieldSelection(config, fieldSelection, categoryTag) {
  const category = categoryTag !== undefined ?
    categoryTag :
    (config?.tag_mapping?.category || categoryTagFromPayload(fieldSelection?.payload_fields) || '')
  const configWithoutCategory = withoutStoredCategory(config)
  if (!fieldSelection && !category) return configWithoutCategory
  const sanitizedSelection = withCategoryPayload(
    sanitizeFieldSelection(fieldSelection || {}),
    category
  )
  const result = { ...configWithoutCategory }
  if (sanitizedSelection.embed_fields.length) result.embed_fields = sanitizedSelection.embed_fields
  if (Object.keys(sanitizedSelection.payload_fields).length) {
    result.payload_fields = sanitizedSelection.payload_fields
  }
  if (sanitizedSelection.group_key.length) result.group_key = sanitizedSelection.group_key
  return result
}

// Read an existing source config back into the field-selection shape the picker component expects.
export function extractFieldSelection(config) {
  return withCategoryPayload(sanitizeFieldSelection({
    embed_fields: config?.embed_fields || [],
    payload_fields: config?.payload_fields || {},
    group_key: config?.group_key || []
  }), extractCategoryTag(config))
}
